import { useEffect, useRef } from 'react';
import {
  screenTimeTracker,
  SKIPPED_SECTIONS,
  type TrainerSectionKey,
} from 'src/utils/screenTimeTracker';
import { CONSENT_KEY, CONSENT_ACCEPTED_EVENT } from 'src/hooks/useConsent';
import { REQUIRE_ANALYTICS_CONSENT } from 'src/config/constants';

/**
 * useScreenTime
 *
 * A React hook that starts timing `sectionName` when `active` is `true`
 * and stops timing it when `active` becomes `false` or the component unmounts.
 *
 * Timing is gated on analytics consent:
 *  - If `analytics_consent !== "accepted"` in localStorage, tracking is skipped.
 *  - When the user accepts consent (dispatches `analytics-consent-accepted`),
 *    all currently active sections start timing immediately without waiting for
 *    a dependency change.
 *
 * Sections listed in SKIPPED_SECTIONS (automated transitions) are always ignored.
 *
 * @param sectionName - One of the TRAINER_SECTIONS values (or any unique string key).
 * @param active      - Whether this section is currently visible/active. Defaults to `true`.
 */
export const useScreenTime = (sectionName: string, active: boolean = true): void => {
  const prevSectionRef = useRef<string | null>(null);

  // ── Primary effect: start/stop on section change, step change, or mount/unmount ──
  useEffect(() => {
    // Never track automated transition pages
    if (SKIPPED_SECTIONS.has(sectionName as TrainerSectionKey)) {
      if (prevSectionRef.current && prevSectionRef.current !== sectionName) {
        screenTimeTracker.stopSection(prevSectionRef.current);
      }
      prevSectionRef.current = sectionName;
      return;
    }

    // When the consent banner is disabled, treat tracking as always allowed.
    const hasConsent =
      !REQUIRE_ANALYTICS_CONSENT || localStorage.getItem(CONSENT_KEY) === 'accepted';

    if (active && hasConsent) {
      screenTimeTracker.startSection(sectionName);
    } else {
      screenTimeTracker.stopSection(sectionName);
    }

    prevSectionRef.current = sectionName;

    return () => {
      screenTimeTracker.stopSection(sectionName);
    };
  }, [sectionName, active]);

  // ── Consent effect: start timing immediately when user accepts ──
  // Runs when the user clicks Accept on the ConsentBanner while this section
  // is already mounted, so we don't wait for a dependency change.
  useEffect(() => {
    const handleConsentAccepted = () => {
      if (active && !SKIPPED_SECTIONS.has(sectionName as TrainerSectionKey)) {
        screenTimeTracker.startSection(sectionName);
      }
    };

    window.addEventListener(CONSENT_ACCEPTED_EVENT, handleConsentAccepted);
    return () => window.removeEventListener(CONSENT_ACCEPTED_EVENT, handleConsentAccepted);
  }, [sectionName, active]);
};
